import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 7.1 visual preview at /preview/lesson/7.1
 * "Reading the room: how the people around you use AI"
 * The small, observable signals in someone's work that reveal how they
 * actually use AI, independent of what they say about it.
 */

export default function LessonPreviewPage7_1() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromPrev />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 7 · Working with people who use AI differently · Lesson 7.1
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Reading the room: how the people around you use AI
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Signals · Posture · Team</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 tell-reading drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="First week on a new team. Three teammates. Three completely different AI patterns. Nobody is saying any of this out loud.">
      <p className="mt-6 text-text/90">
        You started Monday. By Wednesday afternoon you have noticed three
        things, and none of them have come up in any meeting.
      </p>

      <div className="mt-6 space-y-4">
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Teammate 01 · The Slack-to-doc gap
          </p>
          <p className="mt-3 text-text/90">
            In Slack she sounds 24 and casual: lowercase, “lol idk,”
            quick takes. Her project doc, sent the same afternoon, reads like
            a consulting deck. Three-part frameworks. Phrases like
            “cross-functional value drivers.” Same person. Two
            different voices.
          </p>
        </div>
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Teammate 02 · The same-day brief
          </p>
          <p className="mt-3 text-text/90">
            Tuesday morning a topic comes up that nobody on the team has
            researched. By 4:15pm the same day, a three-page brief lands in
            the channel. Polished. Sources cited. The teammate posts:
            “quick first pass, happy to dig deeper.”
          </p>
        </div>
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Teammate 03 · The visible flinch
          </p>
          <p className="mt-3 text-text/90">
            In the standup someone says “we should just throw it at
            ChatGPT.” You watch teammate 03&rsquo;s shoulders tighten. He
            does not say anything. He changes the subject. Later in the day
            you see him quietly re-typing a section of the doc the team had
            already drafted.
          </p>
        </div>
      </div>

      <p className="mt-6 text-text/90">
        None of this is bad. None of it is good. It is information. Three
        teammates, three very different relationships with AI, and you only
        get to work well with them if you can read those patterns on purpose.
      </p>
      <p className="mt-4 text-text/90">
        Lesson 7.1 is about the small signals that show up in someone&rsquo;s
        work, the ones that reveal how they actually use AI. Not so you can
        catch them. So the work goes well.
      </p>
    </Section>
  );
}

/* --- Section 2: From 6.5 --- */
function FromPrev() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Week 6 capstone" title="Week 6 closed your solo work. Week 7 opens your team work.">
      <p className="mt-6 text-text/90">
        Weeks 1 through 6 trained you to do the work yourself: verify, prompt,
        decompose, judge, stand behind it, hold a longer piece together. By
        the end of Week 6 you had a portfolio that proved you can ship.
      </p>
      <p className="mt-4 text-text/90">
        Week 7 is the first time other people enter the work. They will not
        use AI the way you do. Some will lean on it harder. Some will refuse
        to touch it. Most will be quiet about which one they are. Your job
        this week is not to convert anyone. It is to read the room and keep
        the work moving.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="One sentence on what Week 6 trained you to do." />
        <ReviewItem n="02" q="Why Week 7 raises the difficulty: who showed up." />
        <ReviewItem n="03" q="The Week 7 posture, in one phrase." />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The five tells" title="People reveal how they use AI in their work, not their words. Learn what to look for.">
      <Subhead n={1} title="The vocab for the lesson" />
      <VocabCard
        term="Tell"
        definition="A small, observable signal in someone's work that reveals how they actually use AI, independent of what they say about it. Tells show up in word choice, structure, response speed, willingness to push back, and the gap between how they talk and how they write."
        analogy="Poker players have tells: a glance, a pause, a hand that moves to the chips before they speak. They are not lying. They are just leaking information through behavior they are not watching. AI tells work the same way. The teammate is not hiding anything on purpose. The pattern is just visible in the artifact."
        memoryHook="A tell is in the work, not in the words. People will tell you what they think about AI. The artifact tells you what they actually do with it."
        why="If you can read tells, you stop being surprised by your teammates. You know which sections of a doc you can trust without re-reading, where to slow down, who needs you to ask a follow-up before a meeting, and who needs you to back off. Reading tells is how you work well with people whose AI use is different from yours."
      />

      <Subhead n={2} title="The five tells worth learning first" className="mt-12" />
      <p className="mt-4 text-text/90">
        These are the patterns that show up most often on real teams. None of
        them is proof of anything by itself. Two or three of them together
        give you a working read.
      </p>
      <TellsGrid />

      <Subhead n={3} title="The posture: read the room, do not police it" className="mt-12" />
      <p className="mt-4 text-text/90">
        Everyone on a modern team uses AI somehow. You included. The point of
        reading tells is not to build a private file on your coworkers. It is
        to understand the room you are working in so the work goes well.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The two rules
        </p>
        <ol className="mt-3 space-y-3 text-text/90">
          <li><span className="font-mono text-xs text-amber mr-2">01.</span> Read tells about the work, not about the person. A vocabulary shift in a doc is information about that doc, not a verdict on the teammate.</li>
          <li><span className="font-mono text-xs text-amber mr-2">02.</span> Never use a tell to call someone out in public. Tells inform your next question, your follow-up, your edit. They do not inform a callout.</li>
        </ol>
      </div>

      <Subhead n={4} title="Where AI helps and where AI hurts on reading the room" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is useful before the meeting and after it. In the room with people,
        you are on your own. AI cannot see body language, hear a pause, or
        feel the tension change when a topic comes up.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The two-tell rule" className="mt-12" />
      <p className="mt-4 text-text/90">
        One tell is a coincidence. Two tells in the same direction is a
        pattern. Before you change how you work with someone based on what
        you noticed, look for a second tell. If you cannot find one, you do
        not have a read yet. You have a hunch.
      </p>
      <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          The rule
        </p>
        <p className="mt-3 text-text/90">
          One tell, keep watching. Two tells, adjust how you collaborate.
          Three tells, the read is solid enough to bring up directly with the
          teammate (in private, about the work, never as a gotcha).
        </p>
      </div>
    </Section>
  );
}

const TELLS = [
  {
    n: '01',
    name: 'Vocabulary shift',
    body: 'The teammate sounds like a 22-year-old in Slack and like McKinsey in the doc. Casual register in chat, “cross-functional value drivers” in the writeup. The gap is where AI lives. Not a problem on its own; useful to know which voice is theirs.',
  },
  {
    n: '02',
    name: 'Speed mismatch',
    body: 'A polished three-page brief at 11pm on a topic that came up that morning, on a subject the teammate has not worked in before. Fast plus polished plus unfamiliar territory is a real tell. People who actually know a topic write slower and rougher.',
  },
  {
    n: '03',
    name: 'The “as we discussed” tell',
    body: 'The document references framings, terms, or conclusions that were not actually in the meeting. The teammate dropped notes into AI, accepted the structure it gave back, and never re-read against what really happened. The doc is confident about something the room never decided.',
  },
  {
    n: '04',
    name: 'Defense reaction',
    body: 'You ask a small question about one sentence: “Why did we land on this phrasing?” The teammate cannot answer, or gets defensive fast. The sentence is AI\'s, not theirs. They are protecting work they never actually made decisions about. Treat the moment carefully.',
  },
  {
    n: '05',
    name: 'Refusal signal',
    body: 'Someone visibly tenses when AI comes up. They change the subject, push back, or quietly redo work that was already AI-drafted. There is almost always a real story behind it: a class that punished it, a job where it backfired, a value they hold. This is information, not a flaw.',
  },
];

function TellsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {TELLS.map((p) => (
        <div key={p.n} className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">Tell {p.n}</span>
            <h4 className="text-lg font-semibold text-amber" dangerouslySetInnerHTML={{ __html: p.name }} />
          </div>
          <p
            className="mt-3 text-text/90"
            dangerouslySetInnerHTML={{ __html: p.body }}
          />
        </div>
      ))}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Generating likely tell patterns before you join a new team or project.',
      'Drafting non-accusatory follow-up questions for a teammate when one tell stands out.',
      'Mapping the tells you noticed against your own AI habits, to keep yourself honest.',
      'Rehearsing a one-on-one conversation about AI use before you have it.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'Reading the room itself: AI cannot see body language, hear a pause, or feel tension shift.',
      'Deciding who to trust: confidence about a person from one chat session is not a read.',
      'Building a private file: writing down judgments about coworkers turns information into ammunition.',
      'Replacing the direct conversation: at some point the tell becomes a question only you can ask.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Match the tell" title="Three scenes. Three teammates. Pick the tell that best fits what you saw.">
      <p className="mt-6 text-text/90">
        For each scene, two tells could plausibly apply. One is the better
        read. Pick the one that is closest to the evidence in front of you,
        not the one that sounds the most damning.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <TellDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  scene: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    scene:
      'You are on a group project. A teammate submits a section at 1am on a topic the team only discussed that afternoon. Three pages, polished, includes a framework none of you talked about. The next morning in standup he calls it “a quick first pass.”',
    optionA: 'Vocabulary shift: the doc sounds different from how he talks.',
    optionB: 'Speed mismatch: polished output on a new topic, faster than human pace.',
    correct: 'B',
    verdict:
      'The strongest signal here is the gap between the topic being new that afternoon and a polished three-page brief existing eight hours later. Vocabulary shift might also be present, but you have not seen enough of his casual voice to call it. Speed mismatch is the tell with the most evidence. The right move is not to confront him. It is to read his brief carefully and ask one specific question about a claim before the team commits to it.',
  },
  {
    n: '02',
    scene:
      'You ask a teammate on your internship team why the recommendation memo phrased one sentence a particular way. She pauses, says “that is just how it came out,” then says she does not have time to talk about wording and changes the subject.',
    optionA: 'Defense reaction: she cannot answer for a sentence in her own doc.',
    optionB: 'Refusal signal: she does not want to discuss AI at all.',
    correct: 'A',
    verdict:
      'Nothing in the scene shows her tensing about AI as a topic. What you saw was that a small ownership question about one sentence triggered a deflection. That is the defense-reaction tell: the sentence is probably AI\'s and she has not done the work to make it hers. The right move is not to push in the moment. It is to leave room and come back later, in private, with a different framing: “Can we walk through the recommendation section together before it goes out?”',
  },
  {
    n: '03',
    scene:
      'A teammate on a club leadership team writes lowercase “lol same” in Slack twenty times a day. Her draft of the year-end report opens with “In an increasingly dynamic landscape, our organization has demonstrated robust progress across multiple strategic vectors.”',
    optionA: 'Vocabulary shift: Slack voice and doc voice are two different people.',
    optionB: 'The “as we discussed” tell: the doc references things that did not happen.',
    correct: 'A',
    verdict:
      'You do not have evidence that the doc invented framings that were never in meetings. You do have a sharp gap between her casual Slack voice and the consultancy register of her draft. That is the classic vocabulary-shift tell. The right move is not to flag it in a comment. It is to suggest in a 1:1 edit pass that the report would be stronger if it sounded more like her, which is also true.',
  },
];

function TellDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The scene
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: drill.scene }}
      />

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which tell best fits the evidence?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span
                  className="mt-2 block"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That tell could be present, but the scene does not show enough
              evidence for it. Read for the tell with the most direct
              support, not the most dramatic one.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The tell with the strongest evidence' : 'Correct'} · Option {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90" dangerouslySetInnerHTML={{ __html: drill.verdict }} />
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Tell in one sentence." answer="A small, observable signal in someone's work that reveals how they actually use AI, independent of what they say about it." />
        <QuickCheckItem n="Q2" q="The five tells, in two or three words each." answer="Vocabulary shift, speed mismatch, the “as we discussed” tell, defense reaction, refusal signal." />
        <QuickCheckItem n="Q3" q="The two rules of reading tells." answer="Read tells about the work, not the person. Never use a tell to call someone out in public." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on reading the room. Two things AI hurts." answer="Helps: generating likely tell patterns before joining a team, drafting non-accusatory follow-ups, mapping tells against your own habits, rehearsing a 1:1. Hurts: reading the room itself, deciding who to trust, building a private file, replacing the direct conversation." />
        <QuickCheckItem n="Q5" q="The two-tell rule, in one line." answer="One tell is a coincidence. Two tells in the same direction is a pattern worth adjusting your collaboration to." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The five tells.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 7.1 · Lock card · Reading the room
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          People reveal how they use AI in their work, not their words. Read
          tells to work better, not to police anyone.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <LockTile n="01" name="Vocab shift" body="Casual in chat, consultancy in the doc. The gap is the tell." />
          <LockTile n="02" name="Speed mismatch" body="Polished work, new topic, fast. People who know a topic write rougher." />
          <LockTile n="03" name='"As discussed"' body="Doc references framings the meeting never produced. AI scaffolding, accepted unread." />
          <LockTile n="04" name="Defense" body="Cannot answer a small ownership question about one sentence. Probably not theirs." />
          <LockTile n="05" name="Refusal" body="Visible tension when AI comes up. There is usually a real story. Information, not flaw." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The two rules
          </p>
          <p className="mt-2 text-text/90">
            Read tells about the work, not the person. Never use a tell to
            call someone out in public.
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The two-tell rule
          </p>
          <p className="mt-2 text-text/90">
            One tell is a coincidence. Two in the same direction is a pattern
            worth adjusting your collaboration to. Three is the read you can
            bring up in private, about the work, never as a gotcha.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          The artifact tells you what people actually do with AI. Listen to
          the artifact.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p
        className="mt-3 text-sm text-text/85"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 7.2
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The teammate who over-trusts AI
          </h3>
          <p className="mt-3 text-sm text-text/85">
            What to do when someone on your team ships AI output without
            checking it. Vocab: Catch. How to catch the work without
            embarrassing the person, and why catching is a team skill, not
            a snitch move.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 7.1 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From Week 6',
    'The tells',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
